import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../components/AppIcon';
import Header from '../components/ui/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import FAQ from '../components/ui/FAQ';
import { getFAQsForPage } from '../utils/faqContent';
import { generatePageFAQSchema } from '../utils/faqSchema';

const AboutPage = () => {
  const { user } = useAuth();
  
  // Get FAQs for about page
  const aboutFAQs = getFAQsForPage('about');
  const faqSchema = generatePageFAQSchema('about', 'https://mailvalidator.octalchip.com/about', {
    pageTitle: 'About Us',
    pageDescription: 'Learn about EmailValidator, a leading email validation service powered by Octalchip.'
  });
  const teamMembers = [
    {
      name: 'Rina Patel',
      role: 'Director of Operations',
      description: 'Overseeing day-to-day operations and ensuring smooth email validation service delivery across all client engagements.'
    },
    {
      name: 'Rajesh Kumar',
      role: 'Technical Director',
      description: 'Leading technical strategy and ensuring the highest quality standards in all email validation algorithms and API development.'
    },
    {
      name: 'Priya Sharma',
      role: 'Director of Client Relations',
      description: 'Managing client relationships and ensuring exceptional customer satisfaction throughout email validation projects.'
    },
    {
      name: 'Anita Desai',
      role: 'Director of Business Development',
      description: 'Driving business growth and strategic partnerships to expand EmailValidator\'s market presence and opportunities.'
    }
  ];

  const values = [
    {
      icon: 'Target',
      title: 'Innovation First',
      description: 'We stay ahead of email validation technology trends to deliver cutting-edge solutions that drive business growth.'
    },
    {
      icon: 'Users',
      title: 'Client-Centric',
      description: 'Your success is our priority. We build email validation solutions that align with your business goals and user needs.'
    },
    {
      icon: 'Shield',
      title: 'Quality Assurance',
      description: 'Every email validation undergoes rigorous testing to ensure reliability, security, and performance.'
    },
    {
      icon: 'Clock',
      title: 'Timely Delivery',
      description: 'We respect deadlines and deliver validation results on time without compromising on accuracy.'
    }
  ];

  const milestones = [
    { year: '2022', title: 'EmailValidator Founded', description: 'EmailValidator was established with a vision to transform email marketing through accurate validation.' },
    { year: '2023', title: 'First 10,000 Validations', description: 'Successfully processed 10,000+ email validations across various industries.' },
    { year: '2024', title: 'API Launch', description: 'Launched comprehensive API for real-time email validation and bulk processing.' },
    { year: '2025', title: 'Industry Recognition', description: 'Received recognition for 99%+ accuracy and served 100+ satisfied clients.' }
  ];

  return (
    <div className="min-h-screen bg-[#f4faf8]">
      <SEO 
        title="About Us"
        description="Learn about EmailValidator, a leading email validation service powered by Octalchip. Discover our mission, values, and commitment to helping businesses maintain clean email lists. Meet our expert team!"
        keywords="about email validator, email validation company, octalchip team, email validation service provider, company mission, team members, email validation expertise"
        canonical="https://mailvalidator.octalchip.com/about"
        structuredData={faqSchema}
      />
      
      <Header />
        {/* Hero Section */}
        <section id="about-hero" className="bg-white py-20">
          <div className="container mx-auto px-6">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-5xl font-bold text-gray-800 mb-6">
                About EmailValidator
              </h1>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-8"></div>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                We are a cutting-edge email validation service that transforms businesses through accurate email verification. 
                Our mission is to empower businesses with technology that drives email marketing success and deliverability.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00d690] mb-2">25+</div>
                  <div className="text-gray-600">Team Members</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00d690] mb-2">100K+</div>
                  <div className="text-gray-600">Emails Validated</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00d690] mb-2">99%+</div>
                  <div className="text-gray-600">Accuracy Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#00d690] mb-2">100+</div>
                  <div className="text-gray-600">Happy Clients</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section id="our-story" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Story</h2>
                <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-6">From Vision to Reality</h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Founded in 2022, EmailValidator emerged from a simple yet powerful vision: to make accurate email validation 
                    accessible to businesses of all sizes. We started with a small team of passionate developers and data scientists 
                    who believed in the transformative power of clean email lists.
                  </p>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    Today, we've grown into a team of 25+ agile professionals, delivering innovative email validation solutions 
                    with 99%+ accuracy. Our journey has been marked by continuous learning, adaptation, and an unwavering 
                    commitment to client success.
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    From our headquarters in Gujarat, India, we serve clients globally, helping them navigate the email marketing 
                    landscape and achieve their business objectives through accurate email validation.
                  </p>
                </div>
                <div className="bg-white p-8 rounded-lg shadow-lg">
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Our Mission</h4>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    To empower businesses with innovative email validation solutions that drive email marketing success, 
                    optimize deliverability, and create meaningful impact in the digital world.
                  </p>
                  <h4 className="text-xl font-bold text-gray-800 mb-6">Our Vision</h4>
                  <p className="text-gray-600 leading-relaxed">
                    To be the leading email validation partner for businesses worldwide, known for accuracy, 
                    reliability, and client success.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section id="our-values" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => (
                <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow">
                  <div className="w-16 h-16 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name={value.icon} size={32} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Leadership Section */}
        <section id="leadership" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Leadership</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
              {teamMembers.map((member, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-20 h-20 bg-[#00d690]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="User" size={40} className="text-[#00d690]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{member.name}</h3>
                  <p className="text-[#00d690] font-medium mb-3 text-sm">{member.role}</p>
                  <p className="text-gray-600 leading-relaxed text-sm">{member.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Timeline Section */}
        <section id="timeline" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Journey</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto"></div>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div key={index} className="flex items-center">
                    <div className="flex-shrink-0 w-16 h-16 bg-[#00d690] rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {milestone.year}
                    </div>
                    <div className="ml-8 flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section - Only show for non-authenticated users */}
        {!user && (
          <section id="about-cta" className="py-20 bg-[#f4faf8]">
            <div className="container mx-auto px-6 text-center">
              <h2 className="text-4xl font-bold text-gray-800 mb-6">Ready to Validate Your Emails?</h2>
              <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
                Let's discuss your email validation needs and see how we can help improve your email marketing results.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/auth/register">
                  <Button
                    variant="success"
                    size="lg"
                    animation="bounce"
                    className="bg-[#00d690] hover:bg-[#00b876]"
                  >
                    Start Free Trial
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    variant="outline"
                    size="lg"
                    animation="wiggle"
                    className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}
        
        {/* FAQ Section */}
        <section id="faq" className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-gray-600">
                  Learn more about EmailValidator and our company
                </p>
              </div>
              <FAQ items={aboutFAQs} variant="default" />
            </div>
          </div>
        </section>
        
        <Footer />
    </div>
  );
};

export default AboutPage; 